import * as THREE from 'three';
import {CircleSplineIntersectionSolver} from '../../math/CircleSplineIntersectionSolver';
import {LineSplineIntersectionSolver} from '../../math/LineSplineIntersectionSolver';
import type {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import type {SketchLineObject} from '../../mesh/sketch/SketchLineObject';
import type {SketchSplineObject} from '../../mesh/sketch/SketchSplineObject';
import type {SketchStyleSplineObject} from '../../mesh/sketch/SketchStyleSplineObject';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import {
    SketchTrimCurveKind,
    type SketchTrimCurveData,
    type SketchTrimLineData,
} from './SketchTrimTypes';
import {SketchTrimSplineIntersectionDefinitionResolver} from './SketchTrimSplineIntersectionDefinitionResolver';

/**
 * Trim 直线/完整圆与样条族求交服务。
 *
 * 职责：
 * 1. 将草图直线、完整圆和样条对象转换为同一参考面内的纯二维定义；
 * 2. 调用 `math` 层真实参数曲线求交器；
 * 3. 把样条原生参数、直线参数和圆角参数统一映射为 Trim `[0,1]` 参数；
 * 4. 为普通 Trim、Power Trim 与 Inside/Outside 提供角色明确的有限域/支撑线入口。
 */
export class SketchTrimSplineIntersectionService {
    /** 直线与样条真实求交器。 */
    private readonly lineSolver: LineSplineIntersectionSolver;

    /** 完整圆与样条真实求交器。 */
    private readonly circleSolver: CircleSplineIntersectionSolver;

    /** 样条真实数学定义与参数映射解析器。 */
    private readonly definitionResolver: SketchTrimSplineIntersectionDefinitionResolver;

    /**
     * 创建 Trim 样条求交服务。
     *
     * @param lineSolver 直线与样条真实求交器。
     * @param circleSolver 完整圆与样条真实求交器。
     * @param definitionResolver 样条真实数学定义与参数映射解析器。
     */
    public constructor(
        lineSolver: LineSplineIntersectionSolver = new LineSplineIntersectionSolver(),
        circleSolver: CircleSplineIntersectionSolver = new CircleSplineIntersectionSolver(),
        definitionResolver: SketchTrimSplineIntersectionDefinitionResolver
            = new SketchTrimSplineIntersectionDefinitionResolver(),
    ) {
        this.lineSolver = lineSolver;
        this.circleSolver = circleSolver;
        this.definitionResolver = definitionResolver;
    }

    /**
     * 收集普通 Trim 中直线/完整圆与样条族的目标参数。
     *
     * @param target 目标裁剪曲线。
     * @param candidate 候选边界曲线。
     * @returns 目标参数；不属于本服务矩阵或权威样条定义暂不可用时返回 `null`。
     */
    public collectCurveParameters(
        target: SketchTrimCurveData,
        candidate: SketchTrimCurveData,
    ): number[] | null {
        if (this.isSplineData(target) && candidate.curveKind === SketchTrimCurveKind.Line) {
            return this.collectLineParameters(candidate.curve as SketchLineObject, target, true);
        }
        if (target.curveKind === SketchTrimCurveKind.Line && this.isSplineData(candidate)) {
            return this.collectLineParameters(target.curve as SketchLineObject, candidate, false);
        }
        if (this.isSplineData(target) && candidate.curveKind === SketchTrimCurveKind.Circle) {
            return this.collectCircleParameters(candidate.curve as SketchCircleObject, target, true);
        }
        if (target.curveKind === SketchTrimCurveKind.Circle && this.isSplineData(candidate)) {
            return this.collectCircleParameters(target.curve as SketchCircleObject, candidate, false);
        }
        return null;
    }

    /**
     * 收集 Power Trim 拖拽线段与样条的真实交点。
     *
     * @param curveData 普通样条或样式样条裁剪数据。
     * @param segmentStart 拖拽线段起点。
     * @param segmentEnd 拖拽线段终点。
     * @returns 精确交点；非样条或权威定义暂不可用时返回 `null`。
     */
    public collectSegmentIntersections(
        curveData: SketchTrimCurveData,
        segmentStart: THREE.Vector3,
        segmentEnd: THREE.Vector3,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> | null {
        if (!this.isSplineData(curveData)) {
            return null;
        }
        const spline = curveData.curve as SketchSplineObject | SketchStyleSplineObject;
        const definition = this.definitionResolver.resolveDefinition(spline);
        if (!definition) {
            return null;
        }
        const result = this.lineSolver.solveSegment(
            {
                start: spline.referencePlane.worldToLocal(segmentStart),
                end: spline.referencePlane.worldToLocal(segmentEnd),
            },
            definition,
            this.resolveOptions(),
        );

        // 拖拽路径与目标重合时不存在唯一越过顺序，禁止制造密集命中点。
        if (result.overlaps.length > 0) {
            return [];
        }
        return this.dedupeSegmentIntersections(result.points.map((point) => ({
            targetT: this.definitionResolver.normalizeParameter(
                definition,
                point.curveParameter,
                curveData.isClosed,
            ),
            sourceT: THREE.MathUtils.clamp(point.lineParameter, 0, 1),
            point: segmentStart.clone().lerp(
                segmentEnd,
                THREE.MathUtils.clamp(point.lineParameter, 0, 1),
            ),
        })));
    }

    /**
     * 收集 Inside/Outside 无限直线边界与样条目标的真实交点参数。
     *
     * @param target 待裁剪样条目标。
     * @param boundaryLineData 直线边界数据。
     * @returns 目标样条参数；非样条或权威定义暂不可用时返回 `null`。
     */
    public collectLineSupportParameters(
        target: SketchTrimCurveData,
        boundaryLineData: SketchTrimLineData,
    ): number[] | null {
        if (!this.isSplineData(target)) {
            return null;
        }
        const spline = target.curve as SketchSplineObject | SketchStyleSplineObject;
        const definition = this.definitionResolver.resolveDefinition(spline);
        if (!definition) {
            return null;
        }
        const result = this.lineSolver.solveSupportingLine(
            {
                start: spline.referencePlane.worldToLocal(boundaryLineData.start),
                end: spline.referencePlane.worldToLocal(boundaryLineData.end),
            },
            definition,
            this.resolveOptions(),
        );

        // Inside/Outside 只接受离散横穿；重合区间会让内外边界失去唯一性。
        if (result.overlaps.length > 0) {
            return [];
        }
        return this.sortAndDedupeParameters(
            result.points.map((point) => this.definitionResolver.normalizeParameter(
                definition,
                point.curveParameter,
                target.isClosed,
            )),
            target.isClosed,
        );
    }

    /**
     * 按 Trim 参数计算样条真实世界点。
     *
     * @param curveData 普通样条或样式样条裁剪数据。
     * @param normalizedParameter Trim 归一化参数。
     * @returns 真实曲线世界点；非样条或定义暂不可用时返回 `null`。
     */
    public evaluateWorldPoint(
        curveData: SketchTrimCurveData,
        normalizedParameter: number,
    ): THREE.Vector3 | null {
        if (!this.isSplineData(curveData)) {
            return null;
        }
        return this.definitionResolver.evaluateWorldPoint(
            curveData.curve as SketchSplineObject | SketchStyleSplineObject,
            normalizedParameter,
        );
    }

    /**
     * 收集有限直线与样条交点，并映射到指定目标参数。
     *
     * @param line 有限草图直线。
     * @param splineData 样条裁剪数据。
     * @param mapToSplineParameter 是否返回样条参数。
     * @returns 目标参数；样条定义暂不可用时返回 `null`。
     */
    private collectLineParameters(
        line: SketchLineObject,
        splineData: SketchTrimCurveData,
        mapToSplineParameter: boolean,
    ): number[] | null {
        const spline = splineData.curve as SketchSplineObject | SketchStyleSplineObject;
        const definition = this.definitionResolver.resolveDefinition(spline);
        if (!definition) {
            return null;
        }
        const result = this.lineSolver.solveSegment(
            {
                start: spline.referencePlane.worldToLocal(line.startPoint.getPosition()),
                end: spline.referencePlane.worldToLocal(line.endPoint.getPosition()),
            },
            definition,
            this.resolveOptions(),
        );
        if (result.overlaps.length > 0) {
            return [];
        }
        return this.sortAndDedupeParameters(result.points.map((point) => (
            mapToSplineParameter
                ? this.definitionResolver.normalizeParameter(
                    definition,
                    point.curveParameter,
                    splineData.isClosed,
                )
                : THREE.MathUtils.clamp(point.lineParameter, 0, 1)
        )), mapToSplineParameter && splineData.isClosed);
    }

    /**
     * 收集完整圆与样条交点，并映射到指定目标参数。
     *
     * @param circle 完整草图圆。
     * @param splineData 样条裁剪数据。
     * @param mapToSplineParameter 是否返回样条参数。
     * @returns 目标参数；样条定义暂不可用时返回 `null`。
     */
    private collectCircleParameters(
        circle: SketchCircleObject,
        splineData: SketchTrimCurveData,
        mapToSplineParameter: boolean,
    ): number[] | null {
        const spline = splineData.curve as SketchSplineObject | SketchStyleSplineObject;
        const definition = this.definitionResolver.resolveDefinition(spline);
        if (!definition) {
            return null;
        }
        const result = this.circleSolver.solve(
            {
                center: spline.referencePlane.worldToLocal(circle.getCenterWorld()),
                radius: circle.getRadius(),
            },
            definition,
            this.resolveOptions(),
        );
        if (result.overlaps.length > 0) {
            return [];
        }
        return this.sortAndDedupeParameters(result.points.map((point) => (
            mapToSplineParameter
                ? this.definitionResolver.normalizeParameter(
                    definition,
                    point.curveParameter,
                    splineData.isClosed,
                )
                : this.normalizeClosedParameter(point.circleAngle / (Math.PI * 2))
        )), mapToSplineParameter ? splineData.isClosed : true);
    }

    /**
     * 创建区分世界距离、曲线参数和相切角度的统一求交选项。
     * @returns 样条求交数值选项。
     */
    private resolveOptions(): LineSplineIntersectionOptions {
        return {
            distanceTolerance: SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionDistance,
            confusionTolerance: SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionDistance,
        };
    }

    /**
     * 判断裁剪数据是否属于普通样条或样式样条。
     * @param curveData 待判断裁剪数据。
     * @returns 属于样条族时返回 true。
     */
    private isSplineData(curveData: SketchTrimCurveData): boolean {
        return curveData.curveKind === SketchTrimCurveKind.Spline
            || curveData.curveKind === SketchTrimCurveKind.StyleSpline;
    }

    /**
     * 对 Trim 参数排序并按周期接缝规则去重。
     *
     * @param parameters 原始参数。
     * @param isClosed 是否为闭合目标。
     * @returns 排序去重后的参数。
     */
    private sortAndDedupeParameters(parameters: readonly number[], isClosed: boolean): number[] {
        const normalized = parameters.map((parameter) => (
            isClosed ? this.normalizeClosedParameter(parameter) : THREE.MathUtils.clamp(parameter, 0, 1)
        ));
        return normalized
            .sort((left, right) => left - right)
            .filter((parameter, index, values) => (
                index === 0
                || Math.abs(parameter - values[index - 1]!)
                    > SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionParameter
            ));
    }

    /**
     * 将闭合曲线参数规范到 `[0,1)`。
     * @param parameter 原始参数。
     * @returns 周期规范参数。
     */
    private normalizeClosedParameter(parameter: number): number {
        const normalized = ((parameter % 1) + 1) % 1;
        return normalized >= 1 - SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionParameter ? 0 : normalized;
    }

    /**
     * 按拖拽方向排序并按世界点去重 Power Trim 交点。
     *
     * @param intersections 原始交点。
     * @returns 唯一交点列表。
     */
    private dedupeSegmentIntersections(
        intersections: Array<{targetT: number; sourceT: number; point: THREE.Vector3}>,
    ): Array<{targetT: number; sourceT: number; point: THREE.Vector3}> {
        const distanceToleranceSquared = SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionDistance ** 2;
        return intersections
            .sort((left, right) => left.sourceT - right.sourceT)
            .filter((intersection, index, values) => !values.slice(0, index).some((candidate) => (
                candidate.point.distanceToSquared(intersection.point) <= distanceToleranceSquared
            )));
    }
}
